import { useCallback, useRef } from "react";

/**
 * Single-shot timer that protects users from a Trustly lightbox that loads
 * but never reports any panel event ("blank stuck panel" UX). The owning
 * component arms the timer once `Trustly.establish` has been called and
 * clears it on the first sign of life from the SDK (any panel event).
 *
 * - `start()` schedules a single-shot timer; calling it again clears any
 *   previously-scheduled timer first, so it's safe to retry.
 * - `clear()` is idempotent and a no-op if no timer is pending.
 * - When `timeoutMs <= 0` the watchdog is effectively disabled (`start()`
 *   becomes a no-op). Useful for tests or feature flags.
 *
 * The hook intentionally has no opinion on what should happen on timeout
 * (logging, calling host onError, destroying the iframe, etc.); the caller
 * passes an `onTimeout` callback that does whatever it needs.
 */
export const useTrustlyWatchdog = (
  onTimeout: () => void,
  timeoutMs: number
) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    if (timeoutMs <= 0) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onTimeout();
    }, timeoutMs);
  }, [clear, onTimeout, timeoutMs]);

  return { start, clear };
};

export default useTrustlyWatchdog;
